// First-touch attribution capture — runs on every app load.
//
// Persists fbclid / gclid / utm_* into localStorage so the booking flow
// can attach them to the customer record. When a phone or customer_id is
// known, syncs to Supabase via ads-orchestrator?action=capture_attribution.
//
// Wire-up in your entrypoint (e.g. src/index.js):
//   import { captureAttribution, attachToBooking } from "./attribution";
//   captureAttribution();   // call once on app boot
//
// On the booking submit:
//   const attribution = attachToBooking(customerId);   // returns { fbclid, gclid, utm }
//   // pass attribution into your createBooking() payload so it lands on
//   // the bookings row, and the sweep_conversions job will emit it back.

const STORAGE_KEY = "aura_attribution_v1";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

function readStorage() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* private mode, etc. */
  }
}

function getFbp() {
  const m = document.cookie.match(/(?:^|; )_fbp=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function getFbc(fbclid) {
  // Meta's standard _fbc cookie format: fb.1.{timestamp}.{fbclid}
  const m = document.cookie.match(/(?:^|; )_fbc=([^;]+)/);
  if (m) return decodeURIComponent(m[1]);
  if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;
  return null;
}

export function captureAttribution() {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const stored = readStorage();

  const fbclid = params.get("fbclid");
  const gclid  = params.get("gclid") || params.get("wbraid") || params.get("gbraid");

  const utm = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) utm[k] = v;
  }

  const isFirstTouch = !stored.first_touch_at;
  const next = {
    ...stored,
    last_touch_at: new Date().toISOString(),
    last_landing_url: window.location.href,
  };

  if (isFirstTouch) {
    next.first_touch_at  = next.last_touch_at;
    next.landing_url     = window.location.href;
  }

  // Refresh click IDs on every fresh click (they can be re-acquired)
  if (fbclid) next.fbclid = fbclid;
  if (gclid)  next.gclid  = gclid;

  // Merge UTMs only if the new visit has them (don't blank out on direct hits)
  if (Object.keys(utm).length) {
    next.utm = isFirstTouch ? utm : { ...(stored.utm || {}), ...utm };
  }

  // Always refresh Meta cookies (set by Pixel snippet if present)
  const fbp = getFbp();
  const fbc = getFbc(next.fbclid);
  if (fbp) next.fbp = fbp;
  if (fbc) next.fbc = fbc;

  writeStorage(next);
  return next;
}

export function getAttribution() {
  return readStorage();
}

export function clearAttribution() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

// Returns the attribution payload that should be stored on a new booking
// row.  Call right before submitting a booking.
export function attachToBooking() {
  const a = readStorage();
  return {
    fbclid: a.fbclid || null,
    gclid:  a.gclid  || null,
    ad_source:      a.utm?.utm_source   || (a.fbclid ? "meta" : a.gclid ? "google" : null),
    ad_campaign_id: a.utm?.utm_campaign || null,
    utm: a.utm || {},
  };
}

// Push attribution to Supabase for a known customer.  Idempotent;
// the server enforces first-touch on ad_source / first_touch_at.
export async function syncToServer({ supabaseUrl, supabaseAnonKey, customerId, phone, email }) {
  if (!supabaseUrl) return null;
  const a = readStorage();
  if (!a.fbclid && !a.gclid && !Object.keys(a.utm || {}).length) return null;

  const body = {
    customer_id: customerId,
    phone,
    email,
    fbclid: a.fbclid,
    gclid:  a.gclid,
    utm:    a.utm || {},
    landing_url: a.landing_url,
    ad_source:      a.utm?.utm_source   || (a.fbclid ? "meta" : a.gclid ? "google" : null),
    ad_campaign_id: a.utm?.utm_campaign || null,
  };

  try {
    const r = await fetch(`${supabaseUrl}/functions/v1/ads-orchestrator?action=capture_attribution`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify(body),
    });
    return await r.json();
  } catch (e) {
    return { error: e.message };
  }
}
